import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTelegram } from '../../hooks/useTelegram';
import { apiService } from '../../utils/api';
import styles from './AttestationScreen.module.css';
import { Loader } from '../../components/Loader';
import { BackButton } from '../../components/BackButton';
import Select, { StylesConfig } from 'react-select';
import ReactCountryFlag from 'react-country-flag';
import { getData } from 'country-list';
import type { AttestationRequest } from '../../types/api';
type AttestationRequestNoEmail = Omit<AttestationRequest, 'email'>;

// Types for attestation form
type IncomeOption = 'yes' | 'no' | '';
type JointIncomeOption = 'yes' | 'no' | '';
type NetWorthOption = 'yes' | 'no' | '';
type AffirmationOption = boolean;

interface AttestationFormData {
  attestationYear: number;
  individualIncome: IncomeOption;
  jointIncome: JointIncomeOption;
  netWorth: NetWorthOption;
  affirmation: AffirmationOption;
  country: string;
  email: string;
  subscribeToNewsletter: boolean;
}

// Props for the AttestationScreen component
interface AttestationScreenProps {
  onAttestationComplete?: () => void;
}

// Custom country option type for react-select
interface CountryOption {
  value: string;
  label: string;
  code: string;
}

// Convert country-list data to react-select options with flags
const getCountryOptions = (): CountryOption[] => {
  const countries = getData();
  return countries.map(country => ({
    value: country.name,
    label: country.name,
    code: country.code
  }));
};

// Country options for the dropdown
const COUNTRY_OPTIONS: CountryOption[] = getCountryOptions();

// Current year for attestation
const CURRENT_YEAR = new Date().getFullYear();

export const AttestationScreen: React.FC<AttestationScreenProps> = ({ onAttestationComplete }) => {
  const navigate = useNavigate();
  const { isReady, webApp, isTelegram } = useTelegram();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attestationStatus, setAttestationStatus] = useState<'attested_accredited' | 'attested_not_accredited' | 'not_attested' | 'pending' | null>(null);
  const [attestationYear, setAttestationYear] = useState<number | null>(null);
  const [formStep, setFormStep] = useState(1);
  const [animateDirection, setAnimateDirection] = useState<'next' | 'prev'>('next');
  
  const [formData, setFormData] = useState<AttestationFormData>({
    attestationYear: CURRENT_YEAR,
    individualIncome: '',
    jointIncome: '',
    netWorth: '',
    affirmation: false,
    country: '',
    email: '',
    subscribeToNewsletter: true
  });

  const [formErrors, setFormErrors] = useState({
    attestationYear: false,
    individualIncome: false,
    jointIncome: false,
    netWorth: false,
    affirmation: false,
    country: false,
    email: false
  });

  // Check attestation status
  const checkAttestationStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getAttestationStatus();
      
      if (response.has_attestation) {
        if (response.is_accredited) {
          setAttestationStatus('attested_accredited');
        } else {
          setAttestationStatus('attested_not_accredited');
          // If user is attested but not accredited, show appropriate message
        }
        
        // Save the latest attestation year
        if (response.latest_attestation_year) {
          setAttestationYear(response.latest_attestation_year);
        }
      } else {
        setAttestationStatus('not_attested');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error checking attestation status:', err);
      setError('Failed to check attestation status. Please try again.');
      setAttestationStatus('not_attested');
      setLoading(false);
    }
  }, []);

  // Check attestation status on load
  useEffect(() => {
    if (isReady) {
      checkAttestationStatus();
    }
  }, [isReady, checkAttestationStatus]);

  // Form field change handlers
  const handleIndividualIncomeChange = (value: IncomeOption) => {
    setFormData(prev => ({ ...prev, individualIncome: value }));
    setFormErrors(prev => ({ ...prev, individualIncome: false }));
  };

  const handleJointIncomeChange = (value: JointIncomeOption) => {
    setFormData(prev => ({ ...prev, jointIncome: value }));
    setFormErrors(prev => ({ ...prev, jointIncome: false }));
  };

  const handleNetWorthChange = (value: NetWorthOption) => {
    setFormData(prev => ({ ...prev, netWorth: value }));
    setFormErrors(prev => ({ ...prev, netWorth: false }));
  };

  const handleAffirmationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, affirmation: e.target.checked }));
    setFormErrors(prev => ({ ...prev, affirmation: false }));
  };

  const handleCountryChange = (selectedOption: CountryOption | null) => {
    setFormData(prev => ({ ...prev, country: selectedOption ? selectedOption.value : '' }));
    setFormErrors(prev => ({ ...prev, country: false }));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, email: e.target.value }));
    setFormErrors(prev => ({ ...prev, email: false }));
  };

  const handleSubscribeToNewsletterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, subscribeToNewsletter: e.target.checked }));
  };

  // Styles for the country select component
  const countrySelectStyles: StylesConfig<CountryOption, false> = {
    control: (provided, state) => ({
      ...provided,
      padding: '10px',
      borderRadius: '12px',
      border: formErrors.country ? '2px solid #F44336' : 'none',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(75, 79, 71, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
      backgroundColor: 'var(--tg-theme-bg-color, #ffffff)',
      color: 'var(--tg-theme-text-color, #000000)',
      fontSize: '16px',
      cursor: 'pointer',
      minHeight: '50px',
      '&:hover': {
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)'
      },
      transition: 'all 0.3s ease'
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '14px 12px',
      backgroundColor: state.isSelected 
        ? 'rgba(75, 79, 71, 0.15)' 
        : state.isFocused 
          ? 'var(--tg-theme-secondary-bg-color, rgba(75, 79, 71, 0.08))' 
          : 'transparent',
      color: 'var(--tg-theme-text-color, #000000)',
      cursor: 'pointer',
      borderRadius: '6px',
      margin: '2px 4px',
      display: 'flex',
      alignItems: 'center',
      transition: 'background-color 0.2s ease'
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--tg-theme-bg-color, #ffffff)',
      border: '1px solid var(--tg-theme-hint-color, rgba(75, 79, 71, 0.2))',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
      zIndex: 1000,
      position: 'absolute'
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '10px',
      maxHeight: '250px',
      '&::-webkit-scrollbar': {
        width: '0px',
        height: '0px'
      },
      scrollbarWidth: 'none',
      msOverflowStyle: 'none'
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--tg-theme-text-color, #000000)',
      paddingLeft: '20px'
    }),
    input: (provided) => ({
      ...provided,
      color: 'var(--tg-theme-text-color, #000000)'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--tg-theme-hint-color, #999999)',
      paddingLeft: '20px'
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: 'var(--tg-theme-hint-color, #999999)'
    })
  };

  // Custom formatting for the country option with flag
  const formatCountryOption = (option: CountryOption) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <ReactCountryFlag 
        countryCode={option.code} 
        svg 
        style={{
          width: '1.5em',
          height: '1.5em',
          marginRight: '10px',
          borderRadius: '50%',
          objectFit: 'cover'
        }}
      />
      {option.label}
    </div>
  );

  // Form navigation
  const nextStep = () => {
    if (formStep === 1) {
      if (formData.individualIncome === '') {
        setFormErrors(prev => ({ ...prev, individualIncome: true }));
        webApp?.HapticFeedback.notificationOccurred('error');
        return;
      }
      
      if (formData.jointIncome === '') {
        setFormErrors(prev => ({ ...prev, jointIncome: true }));
        webApp?.HapticFeedback.notificationOccurred('error');
        return;
      }
      
      if (formData.netWorth === '') {
        setFormErrors(prev => ({ ...prev, netWorth: true }));
        webApp?.HapticFeedback.notificationOccurred('error');
        return;
      }
    }
    
    webApp?.HapticFeedback.impactOccurred('light');
    setAnimateDirection('next');
    setFormStep(prev => prev + 1);
  };

  const prevStep = () => {
    if (formStep > 1) {
      webApp?.HapticFeedback.impactOccurred('light');
      setAnimateDirection('prev');
      setFormStep(prev => prev - 1);
    }
  };

  // Enhanced email validation function
  const isValidEmail = (email: string): boolean => {
    // More strict email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Handler for submit button click when it's inactive
  const handleSubmitButtonClick = () => {
    // If the form is not ready yet, show errors but don't submit
    if (formStep === 2) {
      const errors = {
        country: formData.country === '',
        email: !formData.email || !isValidEmail(formData.email),
        affirmation: !formData.affirmation
      };
      
      setFormErrors(prev => ({ 
        ...prev, 
        country: errors.country,
        email: errors.email,
        affirmation: errors.affirmation
      }));
      
      webApp?.HapticFeedback.notificationOccurred('error');
    }
  };

  // Validation: email only for Telegram
  const validateForm = (): boolean => {
    const emailRequired = isTelegram;
    const errors = {
      attestationYear: !formData.attestationYear,
      individualIncome: formData.individualIncome === '',
      jointIncome: formData.jointIncome === '',
      netWorth: formData.netWorth === '',
      affirmation: !formData.affirmation,
      country: formData.country === '',
      email: emailRequired ? (!formData.email || !isValidEmail(formData.email)) : false
    };
    setFormErrors(errors);
    return !Object.values(errors).some(Boolean);
  };

  // In submitAttestation for PWA, do not send email
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      webApp?.HapticFeedback.notificationOccurred('error');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      webApp?.HapticFeedback.impactOccurred('medium');
      let attestationPayload: AttestationRequest | AttestationRequestNoEmail;
      if (isTelegram) {
        attestationPayload = {
          attestation_year: formData.attestationYear,
          individual_income_test: formData.individualIncome === 'yes',
          joint_income_test: formData.jointIncome === 'yes',
          net_worth_test: formData.netWorth === 'yes',
          affirmation: formData.affirmation,
          country: formData.country,
          email: formData.email,
          subscribe_to_newsletter: formData.subscribeToNewsletter
        };
      } else {
        attestationPayload = {
          attestation_year: formData.attestationYear,
          individual_income_test: formData.individualIncome === 'yes',
          joint_income_test: formData.jointIncome === 'yes',
          net_worth_test: formData.netWorth === 'yes',
          affirmation: formData.affirmation,
          country: formData.country,
          subscribe_to_newsletter: formData.subscribeToNewsletter
        };
      }
      const response = await apiService.submitAttestation(attestationPayload);
      
      if (response.success) {
        // Check attestation status after successful submission
        const statusResponse = await apiService.getAttestationStatus();
        
        if (statusResponse.has_attestation) {
          if (statusResponse.is_accredited) {
            setAttestationStatus('attested_accredited');
            // Call the onAttestationComplete callback if provided
            if (onAttestationComplete) {
              // Wait a moment to show the success message before redirecting
              setTimeout(() => {
                onAttestationComplete();
              }, 1500);
            }
          } else {
            setAttestationStatus('attested_not_accredited');
            if (statusResponse.latest_attestation_year) {
              setAttestationYear(statusResponse.latest_attestation_year);
            }
          }
        } else {
          // If status hasn't updated for some reason, show pending
          setAttestationStatus('pending');
        }
        
        webApp?.HapticFeedback.notificationOccurred('success');
        
        // If user is accredited, redirect to main screen
        if (statusResponse.has_attestation && statusResponse.is_accredited) {
          setTimeout(() => {
            navigate('/');
          }, 2000);
        }
      } else {
        setError(response.message || 'Failed to submit attestation. Please try again.');
        webApp?.HapticFeedback.notificationOccurred('error');
        setSubmitting(false);
      }
      
    } catch (err) {
      console.error('Error submitting attestation:', err);
      setError('Failed to submit attestation. Please try again.');
      webApp?.HapticFeedback.notificationOccurred('error');
      setSubmitting(false);
    }
  };

  // Render progress bar
  const renderProgressBar = () => {
    return (
      <div className={styles.progressContainer}>
        <div className={styles.progressBar}>
          <div 
            className={styles.progressFill} 
            style={{ width: `${(formStep / 2) * 100}%` }}
          />
        </div>
        <div className={styles.progressSteps}>
          <div className={`${styles.progressStep} ${formStep >= 1 ? styles.activeStep : ''}`}>1</div>
          <div className={`${styles.progressStep} ${formStep >= 2 ? styles.activeStep : ''}`}>2</div>
        </div>
      </div>
    );
  };

  // Render income questions form
  const renderIncomeForm = () => {
    // Check if all three fields are selected
    const allFieldsSelected = formData.individualIncome !== '' && 
                             formData.jointIncome !== '' && 
                             formData.netWorth !== '';
                             
    return (
      <div className={`${styles.formContent} ${styles[`animate-${animateDirection}`]}`}>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Does your annual individual income exceed $200,000?
          </label>
          <div className={styles.radioGroup}>
            <div 
              className={`${styles.radioOption} ${formData.individualIncome === 'yes' ? styles.selected : ''}`}
              onClick={() => handleIndividualIncomeChange('yes')}
            >
              <div className={styles.radioButton}>
                {formData.individualIncome === 'yes' && <div className={styles.radioButtonInner} />}
              </div>
              <span>Yes</span>
            </div>
            <div 
              className={`${styles.radioOption} ${formData.individualIncome === 'no' ? styles.selected : ''}`}
              onClick={() => handleIndividualIncomeChange('no')}
            >
              <div className={styles.radioButton}>
                {formData.individualIncome === 'no' && <div className={styles.radioButtonInner} />}
              </div>
              <span>No</span>
            </div>
          </div>
          {formErrors.individualIncome && <div className={styles.fieldError}>Please select an option</div>}
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Does your annual joint household income exceed $300,000?
          </label>
          <div className={styles.radioGroup}>
            <div 
              className={`${styles.radioOption} ${formData.jointIncome === 'yes' ? styles.selected : ''}`}
              onClick={() => handleJointIncomeChange('yes')}
            >
              <div className={styles.radioButton}>
                {formData.jointIncome === 'yes' && <div className={styles.radioButtonInner} />}
              </div>
              <span>Yes</span>
            </div>
            <div 
              className={`${styles.radioOption} ${formData.jointIncome === 'no' ? styles.selected : ''}`}
              onClick={() => handleJointIncomeChange('no')}
            >
              <div className={styles.radioButton}>
                {formData.jointIncome === 'no' && <div className={styles.radioButtonInner} />}
              </div>
              <span>No</span>
            </div>
          </div>
          {formErrors.jointIncome && <div className={styles.fieldError}>Please select an option</div>}
        </div>
        
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Do you have a net worth exceeding $1 million (excluding primary residence)?
          </label>
          <div className={styles.radioGroup}>
            <div 
              className={`${styles.radioOption} ${formData.netWorth === 'yes' ? styles.selected : ''}`}
              onClick={() => handleNetWorthChange('yes')}
            >
              <div className={styles.radioButton}>
                {formData.netWorth === 'yes' && <div className={styles.radioButtonInner} />}
              </div>
              <span>Yes</span>
            </div>
            <div 
              className={`${styles.radioOption} ${formData.netWorth === 'no' ? styles.selected : ''}`}
              onClick={() => handleNetWorthChange('no')}
            >
              <div className={styles.radioButton}>
                {formData.netWorth === 'no' && <div className={styles.radioButtonInner} />}
              </div>
              <span>No</span>
            </div>
          </div>
          {formErrors.netWorth && <div className={styles.fieldError}>Please select an option</div>}
        </div>

        <div className={styles.navigationButtons}>
          <button 
            type="button" 
            className={`${styles.nextButton} ${allFieldsSelected ? styles.activeButton : ''}`}
            onClick={nextStep}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  // Render personal info form
  const renderPersonalInfoForm = () => {
    // For Telegram, email is required; for PWA — not required
    const emailRequired = isTelegram;
    const allPersonalInfoComplete =
      formData.country !== '' &&
      (!emailRequired || (formData.email !== '' && isValidEmail(formData.email))) &&
      formData.affirmation === true;
    const selectedCountry = COUNTRY_OPTIONS.find(option => option.value === formData.country);
    return (
      <div className={`${styles.formContent} ${styles[`animate-${animateDirection}`]}`}> 
        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="country">
            Country of Origin
          </label>
          <div className={styles.countrySelectWrapper}>
            <Select<CountryOption>
              id="country"
              className={styles.countrySelect}
              classNamePrefix="react-select"
              options={COUNTRY_OPTIONS}
              value={selectedCountry || null}
              onChange={handleCountryChange}
              formatOptionLabel={formatCountryOption}
              styles={countrySelectStyles}
              placeholder="Select your country"
              isClearable={false}
              isSearchable={true}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          {formErrors.country && <div className={styles.fieldError}>Please select your country</div>}
        </div>
        {isTelegram && (
          <div className={styles.formGroup}>
            <label className={styles.label} htmlFor="email">
              Email *
            </label>
            <input
              id="email"
              type="email"
              name="email"
              className={`${styles.input} ${formErrors.email ? styles.inputError : ''}`}
              value={formData.email}
              onChange={handleEmailChange}
              placeholder="Enter your email"
              required={emailRequired}
            />
            {formErrors.email && <div className={styles.fieldError}>Please enter a valid email address</div>}
          </div>
        )}
        <div className={styles.formGroup}>
          <div className={styles.checkboxContainer}>
            <input
              type="checkbox"
              id="subscribeToNewsletter"
              className={styles.checkbox}
              checked={formData.subscribeToNewsletter}
              onChange={handleSubscribeToNewsletterChange}
            />
            <label htmlFor="subscribeToNewsletter" className={styles.checkboxLabel}>
              Receive monthly market insights
            </label>
          </div>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Confirmation
          </label>
          <div className={styles.checkboxContainer}>
            <input
              type="checkbox"
              id="affirmation"
              className={styles.checkbox}
              checked={formData.affirmation}
              onChange={handleAffirmationChange}
            />
            <label htmlFor="affirmation" className={styles.checkboxLabel}>
              I affirm that the information provided is true and accurate
            </label>
          </div>
          {formErrors.affirmation && <div className={styles.fieldError}>You must affirm that the information is true</div>}
        </div>
        <div className={styles.navigationButtons}>
          <BackButton onClick={prevStep} />
          <button 
            type="submit" 
            className={`${styles.submitButton} ${allPersonalInfoComplete ? styles.activeButton : ''}`}
            disabled={submitting}
            onClick={!allPersonalInfoComplete ? handleSubmitButtonClick : undefined}
          >
            {submitting ? 'Submitting...' : 'Submit Attestation'}
          </button>
        </div>
      </div>
    );
  };

  // Render content based on status
  const renderContent = () => {
    if (loading) {
      return <Loader fullscreen />;
    }
    
    if (attestationStatus === 'attested_accredited') {
      return (
        <div className={styles.successContainer}>
          <div className={styles.successIcon}>✓</div>
          <h2 className={styles.successTitle}>You are attested and accredited</h2>
          <p className={styles.successText}>Redirecting to the main screen...</p>
        </div>
      );
    }
    
    if (attestationStatus === 'attested_not_accredited') {
      return (
        <div className={styles.warningContainer}>
          <div className={styles.warningIcon}>⚠️</div>
          <h2 className={styles.warningTitle}>You are attested but not accredited</h2>
          <p className={styles.warningText}>
            Your attestation for {attestationYear} has been accepted, but you do not meet the criteria for an accredited investor.
            This may limit your access to certain investment opportunities.
          </p>
          <button 
            className={styles.continueButton}
            onClick={() => navigate('/')}
          >
            Continue
          </button>
        </div>
      );
    }
    
    if (attestationStatus === 'pending') {
      return (
        <div className={styles.successContainer}>
          <div className={styles.pendingIcon}>⏳</div>
          <h2 className={styles.successTitle}>Attestation Submitted</h2>
          <p className={styles.successText}>Your attestation is being processed. You will be redirected to the main screen.</p>
        </div>
      );
    }
    
    return (
      <form className={styles.form} onSubmit={handleSubmit}>
        <div>
          <h1 className={styles.title}>Investor Attestation</h1>
          <p className={styles.description}>
            Please complete this form to verify your accredited investor status.
          </p>
        </div>
        
        {error && <div className={styles.error}>{error}</div>}
        
        {renderProgressBar()}
        
        {formStep === 1 && renderIncomeForm()}
        {formStep === 2 && renderPersonalInfoForm()}
      </form>
    );
  };

  return (
    <div className={styles.container}>
      {renderContent()}
    </div>
  );
}; 